import Reveal from "@/components/Reveal";
import ImagePlate from "@/components/ImagePlate";
import VocabTag from "@/components/VocabTag";
import { GIGA_IMAGES } from "@/lib/images";

/**
 * Strategic Importance — 40/60 split:
 * left: portrait infrastructure plate
 * right: commanding type + mono metric readouts
 */
export default function StrategicImportance() {
  return (
    <section
      id="strategic"
      data-testid="strategic-section"
      className="border-b border-[color:var(--g1-rule)] bg-[color:var(--g1-paper)]/60"
    >
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 lg:px-14 py-24 md:py-32 lg:py-40">
        <div className="grid grid-cols-12 gap-8 mb-14 md:mb-20 items-start">
          <div className="col-span-12 md:col-span-3 lg:col-span-2">
            <Reveal>
              <div className="g1-label">SEC. 06</div>
              <div className="g1-eyebrow mt-2">Sovereignty</div>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-6 lg:col-span-7 lg:col-start-4">
            <Reveal delay={60}>
              <h2
                data-testid="strategic-heading"
                className="g1-h2 text-[36px] md:text-[64px] lg:text-[84px] max-w-[20ch]"
              >
                Onshored{" "}
                <span className="g1-display-italic">by design.</span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-3 lg:col-span-2 md:col-start-10 lg:col-start-11 text-right">
            <Reveal>
              <VocabTag kind="PRODUCTION" />
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          {/* Left: infrastructure plate */}
          <div className="col-span-12 lg:col-span-5">
            <Reveal delay={100}>
              <ImagePlate
                src={GIGA_IMAGES.infrastructureCorridor}
                alt="Brutalist industrial infrastructure corridor"
                aspect="3/4"
                cornersLight
              />
            </Reveal>
          </div>

          {/* Right: readout + copy */}
          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <Reveal delay={140}>
              <p className="g1-lede text-[color:var(--g1-ink)]">
                Pharmaceutical manufacturing is national infrastructure.{" "}
                <span className="g1-display-italic">
                  So is the platform that produces it.
                </span>
              </p>
            </Reveal>

            <Reveal delay={200}>
              <p className="font-sans text-[15px] leading-relaxed text-[color:var(--g1-ink-2)] mt-6 max-w-[48ch]">
                Whoever owns both the intelligence and the production capacity
                sets the terms on which domestic healthcare is made. Giga One
                treats this as a matter of industrial policy, not product
                strategy.
              </p>
            </Reveal>

            <Reveal delay={260}>
              <div className="mt-10 md:mt-14 border-t border-[color:var(--g1-ink)]">
                {[
                  ["I.", "Domestic production capacity", "CAPACITY"],
                  ["II.", "Healthcare supply resilience", "RESILIENCE"],
                  ["III.", "Critical infrastructure class", "CLASS"],
                  ["IV.", "Sovereign control of the stack", "CONTROL"],
                ].map(([n, label, tag]) => (
                  <div
                    key={label}
                    className="flex items-baseline gap-6 py-4 border-b border-[color:var(--g1-rule)]"
                  >
                    <span className="g1-label text-[color:var(--g1-ink-3)] w-8 shrink-0">
                      {n}
                    </span>
                    <span className="font-serif text-xl md:text-2xl text-[color:var(--g1-ink)] flex-1">
                      {label}
                    </span>
                    <span className="g1-label text-[color:var(--g1-ink-3)] hidden sm:inline">
                      {tag}
                    </span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
